'use client'

import Link from 'next/link'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import CTASection from '@/components/CTASection'
import ProjectCard from '@/components/ProjectCard'
import { useI18n } from '@/lib/i18n/context'
import { CarteEssence } from '@/components/CarteEssence'
import type { LevierAgricole } from '@/data/leviers-agricoles'
import { getScoreLabel } from '@/data/cobenefices-base'
import type { CoBenefice } from '@/data/cobenefices-base'
import { DIMENSION_COLORS, DIMENSION_ICONS } from '@/data/impact-dimensions'
// Les deux listes de types sont partagées avec le serveur (`fiche-projet.ts`) :
// la section « Leviers agricoles » est affichée exactement pour les types dont
// les leviers sont calculés. Module sans données : rien ne part au navigateur.
import { AGRI_TYPES, FORESTRY_TYPES } from '@/data/project-types'
import ImpactRadar from '@/components/ImpactRadar'
import Breadcrumbs from '@/components/Breadcrumbs'
import LazyMount from '@/components/LazyMount'
import { contactCtaHref } from '@/lib/projets/contact-cta'
import type { DonneesFiche, ProjetAffiche } from './fiche-projet'
import { MENTION_ILLUSTRATION } from '@/data/illustrations-projets'
// Type seul : le module du journal public ne part pas dans le paquet du navigateur.
import type { EntreePublique } from '@/lib/journal-public'
// Textes partagés avec la Synthèse de l'espace contributeur : module sans données
import { explicationODD, leviersDesTextes, textesFiche } from './fiche-textes'
import { getTimeline } from './chronologie'
import { Pictogram } from '@/components/Pictogram'

/**
 * Ce composant porte `'use client'` : tout ce qu'il importe part dans le
 * paquet du navigateur. Il importait real-projects.json (1,5 Mo),
 * project-scores.json (1 Mo), project-photos.json, le catalogue des essences
 * et celui des leviers — pour afficher UN projet. Ces lectures se font
 * désormais côté serveur dans `fiche-projet.ts`, et le résultat arrive par la
 * propriété `donnees` (audit performance A1, 20 septembre 2026).
 *
 * Règle à tenir : n'importer ici que des modules SANS données de catalogue.
 * Les `import type` sont effacés à la compilation et ne comptent pas.
 */

const MiniMap = dynamic(() => import('@/components/MiniMap'), { ssr: false })

// ─── SVG Icons ───

function IconSurface({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 3v18" />
    </svg>
  )
}

function IconCO2({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18M3 12h18" />
    </svg>
  )
}

function IconTree({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22V8" />
      <path d="m6 12 6-8 6 8" />
      <path d="m7 17 5-6 5 6" />
    </svg>
  )
}

function IconDensity({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  )
}

function IconChart({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  )
}

function IconLeaf({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 20A7 7 0 0 1 9.8 6.9C15.5 4.9 17 3.5 19 1c1 2 2 4.5 1 8-1 3.5-1.5 5.5-4 8.5" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6" />
    </svg>
  )
}

function IconPin({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function IconCalendar({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function IconClipboard({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
    </svg>
  )
}

function IconTarget({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  )
}

function IconLink({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  )
}

function IconWrench({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  )
}

// ─── Co-bénéfice SVG icons ───

function CoBeneficeIcon({ type, color, size = 20 }: { type: string; color: string; size?: number }) {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

  switch (type) {
    case 'biodiversite':
      return (
        <svg {...props}>
          <path d="M12 3c-1.2 4-4 6-8 6 0 5 3.5 9.5 8 12 4.5-2.5 8-7 8-12-4 0-6.8-2-8-6z" />
          <path d="M12 9v12" />
          <path d="M8 14l4-3 4 3" />
        </svg>
      )
    case 'eau':
      return (
        <svg {...props}>
          <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
        </svg>
      )
    case 'sol':
      return (
        <svg {...props}>
          <path d="M2 22h20" />
          <path d="M6 18c2-3 3-6 3-10" />
          <path d="M12 18c0-4 1.5-7 4-10" />
          <path d="M18 18c-1-2-1.5-5-1-8" />
          <circle cx="8" cy="6" r="1.5" fill={color} />
          <circle cx="15" cy="5" r="1.5" fill={color} />
        </svg>
      )
    case 'paysage':
      return (
        <svg {...props}>
          <path d="M2 20L8.5 10 13 16l3.5-4L22 20z" />
          <circle cx="17" cy="6" r="2.5" />
        </svg>
      )
    case 'social':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      )
    default:
      return <IconLeaf className={`w-[${size}px] h-[${size}px]`} />
  }
}

// ─── Photo Gallery with lightbox ───

import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

function LightboxPortal({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])
  if (!mounted) return null
  return createPortal(children, document.body)
}

/**
 * Image de galerie : vraie photo (légende facultative) ou illustration prise
 * sur un projet comparable (src/data/illustrations-projets.ts), qui porte
 * TOUJOURS sa légende « Illustration : … » et une pastille sur sa vignette.
 */
type ImageGalerie = { src: string; alt: string; legende?: string; illustration?: boolean }
type PhotoFiche = { src: string; alt: string; legende?: string }
type CouvertureFiche = { url: string; alt: string; legende?: string }

function versGalerie(photos: PhotoFiche[], illustrations: PhotoFiche[]): ImageGalerie[] {
  return [...photos, ...illustrations.map((i) => ({ ...i, illustration: true }))]
}

/** Pastille posée sur la vignette d'une illustration. */
function PastilleIllustration() {
  return (
    <span className="absolute left-1 bottom-1 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white">
      {MENTION_ILLUSTRATION}
    </span>
  )
}

/** Légende de la photo affichée dans la visionneuse. */
function LegendeVisionneuse({ image }: { image?: ImageGalerie }) {
  if (!image?.legende) return null
  return (
    <p className={`px-6 pb-3 text-center text-sm ${image.illustration ? 'text-amber-100' : 'text-white/75'}`}>{image.legende}</p>
  )
}

/** Thumbnails displayed inside the Hero, right column */
function HeroThumbnails({ photos, illustrations = [], coverImage, isFr }: { photos: PhotoFiche[]; illustrations?: PhotoFiche[]; coverImage: CouvertureFiche; isFr: boolean }) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Thumbnails show only additional photos (cover is already the hero background)
  // But lightbox includes cover as first image for full navigation
  const thumbnailImages = versGalerie(photos, illustrations)
  const allImages: ImageGalerie[] = [{ src: coverImage.url, alt: coverImage.alt, legende: coverImage.legende }, ...thumbnailImages]

  const goPrev = useCallback(() => setLightboxIndex(i => Math.max(0, i - 1)), [])
  const goNext = useCallback(() => setLightboxIndex(i => Math.min(allImages.length - 1, i + 1)), [allImages.length])
  const close = useCallback(() => setLightboxOpen(false), [])

  useEffect(() => {
    if (!lightboxOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [lightboxOpen, close, goPrev, goNext])

  if (thumbnailImages.length === 0) return null

  // Show max 4 thumbnails (excluding cover), last one shows "+N" overlay
  const maxVisible = 4
  const visibleThumbs = thumbnailImages.slice(0, maxVisible)
  const remaining = thumbnailImages.length - maxVisible

  return (
    <>
      <div className="hidden md:flex flex-col gap-2 flex-shrink-0">
        {visibleThumbs.map((img, i) => (
          <button
            key={i}
            onClick={() => { setLightboxIndex(i + 1); setLightboxOpen(true) }}
            className="relative w-28 h-20 lg:w-32 lg:h-24 rounded-lg overflow-hidden border-2 border-white/30 shadow-lg hover:border-white/60 transition-all cursor-pointer flex-shrink-0"
          >
            <Image
              src={img.src}
              alt={img.alt}
              fill
              className="object-cover hover:scale-105 transition-transform duration-300"
              sizes="128px"
            />
            {img.illustration && <PastilleIllustration />}
            {/* "+N more" overlay on last thumbnail */}
            {i === maxVisible - 1 && remaining > 0 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white font-bold text-sm">+{remaining}</span>
              </div>
            )}
          </button>
        ))}
        <button
          onClick={() => { setLightboxIndex(0); setLightboxOpen(true) }}
          className="text-white/80 text-xs hover:text-white transition-colors text-center mt-1"
        >
          <span className="inline-flex items-center gap-1"><Pictogram name="camera" className="w-3.5 h-3.5" /> {allImages.length} photos</span>
        </button>
      </div>

      {/* Mobile: horizontal strip below hero handled by PhotoGallery */}

      {/* Lightbox */}
      {lightboxOpen && (
        <LightboxPortal>
          <div
            className="fixed inset-0 flex flex-col"
            style={{ zIndex: 99999, background: '#000' }}
            role="dialog"
            aria-modal="true"
            aria-label="Galerie photos"
          >
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0">
              <span className="text-white/60 text-sm">
                {lightboxIndex + 1} / {allImages.length}
              </span>
              <button onClick={close} className="text-white/60 hover:text-white text-2xl px-2" aria-label="Fermer">✕</button>
            </div>
            <div className="flex-1 flex items-center justify-center relative px-4 pb-4">
              <button onClick={goPrev} disabled={lightboxIndex === 0} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-4xl disabled:opacity-20 z-10" aria-label="Précédent">‹</button>
              <div className="relative max-w-[90vw] max-h-[80vh] w-full h-full flex items-center justify-center">
                <Image
                  src={allImages[lightboxIndex]?.src ?? ''}
                  alt={allImages[lightboxIndex]?.alt ?? ''}
                  fill
                  className="object-contain"
                  sizes="90vw"
                />
              </div>
              <button onClick={goNext} disabled={lightboxIndex === allImages.length - 1} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-4xl disabled:opacity-20 z-10" aria-label="Suivant">›</button>
            </div>
            <LegendeVisionneuse image={allImages[lightboxIndex]} />
            <div className="flex gap-2 justify-center py-3 overflow-x-auto px-4 flex-shrink-0">
              {allImages.map((img, i) => (
                <button key={i} onClick={() => setLightboxIndex(i)} className={`relative w-16 h-12 rounded overflow-hidden border-2 flex-shrink-0 transition-all ${i === lightboxIndex ? 'border-white scale-105' : 'border-white/20 opacity-60 hover:opacity-100'}`}>
                  <Image src={img.src} alt={img.alt} width={64} height={48} className="w-full h-full object-cover" />
                  {img.illustration && <PastilleIllustration />}
                </button>
              ))}
            </div>
          </div>
        </LightboxPortal>
      )}
    </>
  )
}

function PhotoGallery({ photos, illustrations = [], coverImage, isFr }: { photos: PhotoFiche[]; illustrations?: PhotoFiche[]; coverImage: CouvertureFiche; isFr: boolean }) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Gallery strip shows only additional photos (cover is already the hero)
  const galleryImages = versGalerie(photos, illustrations)
  // Lightbox includes all images (cover + additional)
  const allImages: ImageGalerie[] = [{ src: coverImage.url, alt: coverImage.alt, legende: coverImage.legende }, ...galleryImages]

  const goPrev = useCallback(() => setLightboxIndex(i => Math.max(0, i - 1)), [])
  const goNext = useCallback(() => setLightboxIndex(i => Math.min(allImages.length - 1, i + 1)), [allImages.length])
  const close = useCallback(() => setLightboxOpen(false), [])

  // Keyboard navigation + body scroll lock
  useEffect(() => {
    if (!lightboxOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [lightboxOpen, close, goPrev, goNext])

  if (galleryImages.length === 0) return null

  return (
    <>
      <section className="bg-cream pt-4 pb-2">
        <div className="container-site">
          <button
            onClick={() => { setLightboxIndex(0); setLightboxOpen(true) }}
            className="text-sm text-forest font-medium hover:underline flex items-center gap-1.5 mb-3"
          >
            <Pictogram name="camera" className="w-4 h-4" /> {isFr ? `Voir les ${allImages.length} photos` : `View all ${allImages.length} photos`}
          </button>
          <div className="overflow-x-auto pb-2 -mx-2">
            <div className="flex gap-3 px-2" style={{ minWidth: 'min-content' }}>
              {galleryImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => { setLightboxIndex(i + 1); setLightboxOpen(true) }}
                  className="relative w-36 h-28 md:w-44 md:h-32 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                >
                  <Image
                    src={img.src}
                    alt={img.alt}
                    width={176}
                    height={128}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                  {img.illustration && <PastilleIllustration />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox — rendu via portail pour ignorer tout z-index parent */}
      {lightboxOpen && (
        <LightboxPortal>
          <div
            className="fixed inset-0 flex flex-col"
            style={{ zIndex: 99999, background: '#000' }}
            role="dialog"
            aria-modal="true"
            aria-label="Galerie photos"
          >
            {/* Top bar — counter + close */}
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0">
              <span className="text-white/60 text-sm">
                {lightboxIndex + 1} / {allImages.length}
              </span>
              <button
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl leading-none transition-colors"
                onClick={close}
                aria-label="Fermer"
              >&times;</button>
            </div>

            {/* Image area — flex-grow pour occuper tout l'espace restant */}
            <div className="flex-1 relative flex items-center justify-center px-16 pb-6 min-h-0">
              {/* Left arrow */}
              {lightboxIndex > 0 && (
                <button
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white text-xl transition-colors"
                  onClick={goPrev}
                  aria-label="Photo précédente"
                >&lsaquo;</button>
              )}

              {/* Right arrow */}
              {lightboxIndex < allImages.length - 1 && (
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white text-xl transition-colors"
                  onClick={goNext}
                  aria-label="Photo suivante"
                >&rsaquo;</button>
              )}

              {/* Image */}
              <Image
                src={allImages[lightboxIndex]?.src ?? ''}
                alt={allImages[lightboxIndex]?.alt ?? ''}
                width={1400}
                height={900}
                className="max-w-full max-h-full object-contain rounded-lg select-none"
                priority
              />
            </div>
            <LegendeVisionneuse image={allImages[lightboxIndex]} />

            {/* Click backdrop to close */}
            <div className="absolute inset-0 -z-10" onClick={close} />
          </div>
        </LightboxPortal>
      )}
    </>
  )
}

// ─── Constants ───

interface ProjectDetailProps {
  /** Uniquement les champs affichés : voir `ProjetAffiche` (constat I-1). */
  project: ProjetAffiche
  /** Tout ce qui vient des catalogues, préparé côté serveur (fiche-projet.ts). */
  donnees: DonneesFiche
  /** Journal public SOBRE, déjà réduit côté serveur (src/lib/journal-public.ts). */
  journal?: EntreePublique[]
  /** Pages région et méthode du projet, quand elles existent (src/lib/seo/agregations-projets.ts). */
  agregats?: {
    region: { nom: string; href: string } | null
    methode: { nom: string; href: string } | null
  }
}

// ─── Main component ───

export default function ProjectDetail({ project, donnees, journal = [], agregats }: ProjectDetailProps) {
  const {
    photos,
    illustrations,
    coverImage,
    odds,
    coBenefices,
    impactResult,
    textesCobenefices,
    presentationRedigee,
    situationDepart,
    essences: essencesDetailed,
    leviers,
    temoignages,
    projetsSimilaires: similarProjects,
    retombees,
  } = donnees
  const { t, locale } = useI18n()
  const isFr = locale !== 'en'
  const numFmt = new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR')
  const p = (path: string) => (locale === 'en' ? `/en${path}` : path)

  const methods = t.methods as Record<string, { name: string; description: string; duration: string | null }>
  const ctx = methods[project.methode] ?? null
  const typeLabelsMap = t.typeLabels as Record<string, string>
  const typeLabel = typeLabelsMap[project.type] || project.methode

  const isForestry = FORESTRY_TYPES.includes(project.type)
  const isAgri = AGRI_TYPES.includes(project.type)

  // Les essences ne servent plus qu'à compter, et `donnees.essences` porte
  // exactement les mêmes entrées (même découpage côté serveur) : la chaîne
  // brute `project.essences` n'a donc plus besoin de partir au navigateur.
  const essencesList = essencesDetailed

  const statut = project.statut.toLowerCase()
  const isLabellise = statut === 'labellisé'
  const isCertifie = statut === 'certifié'
  const isNotifie = statut === 'notifié'
  const statusText = isLabellise ? t.projetDetail.statusLabeled
    : isCertifie ? t.projetDetail.statusCertified : project.statut

  const surfaceStr = project.surface > 0 ? numFmt.format(project.surface) : ''
  const co2Str = project.co2Total > 0 ? numFmt.format(Math.round(project.co2Total)) : ''
  const sectorLabel = isForestry ? isFr ? 'Projet forestier' : 'Forestry project' : isAgri ? isFr ? 'Projet agricole' : 'Agricultural project' : 'Projet'

  const timeline = getTimeline(project, locale)
  // Leviers RÉELLEMENT renseignés du projet (pas ceux de la méthode) : ils précisent les textes
  const leviersDuProjet = leviersDesTextes(leviers)
  // Présentation et textes des dimensions : même source que la fenêtre
  // « détail du projet » des pages de vote (fiche-textes.ts, `textesFiche`)
  const textes = textesFiche(project, { isFr, typeLabel, essences: essencesList, leviers: leviersDuProjet, presentationRedigee, textesCobenefices })

  return (
    // `pb-24` compense la barre d'action mobile fixe en bas d'écran (voir plus
    // bas) : sans cette marge, elle masquerait la fin de la page sur mobile.
    // Rien à compenser sur `lg+`, où la barre n'existe pas.
    <div className="pb-24 lg:pb-0">
      {/* ═══════ HERO SECTION — Image + thumbnails column on right ═══════ */}
      <section className="relative min-h-[480px] md:min-h-[580px] overflow-hidden">
        <Image
          src={coverImage.url}
          alt={coverImage.alt}
          fill
          className="object-cover"
          priority
          fetchPriority="high"
          sizes="100vw"
        />
        {/* Gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/15" />
        {/* Légende de la photo de couverture, quand le catalogue en porte une (date, lieu de prise de vue) */}
        {coverImage.legende && (
          <p className="absolute bottom-1.5 right-3 z-[1] max-w-[85%] text-right text-[10px] leading-snug text-white/75 md:right-5 md:max-w-xl md:text-[11px]">
            {coverImage.legende}
          </p>
        )}

        {/* Hero content: left text + right thumbnails */}
        <div className="absolute inset-0 flex items-end">
          <div className="container-site w-full pb-8 md:pb-10">
            <div className="flex items-end gap-6">
              {/* Left: text content */}
              <div className="flex-1 min-w-0">
                {/* Breadcrumb */}
                <div className="mb-3">
                  <Breadcrumbs
                    variant="light"
                    items={[
                      { label: isFr ? 'Accueil' : 'Home', href: p('/') },
                      { label: isFr ? 'Projets' : 'Projects', href: p('/projets') },
                      ...(agregats?.region ? [{ label: agregats.region.nom, href: agregats.region.href }] : []),
                      { label: project.title },
                    ]}
                  />
                </div>
                {/* Badges row */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="bg-white/15 backdrop-blur-sm text-amber text-xs font-semibold px-3 py-1 rounded-full">
                    {typeLabel}
                  </span>
                  <span className="bg-white/15 backdrop-blur-sm text-white/90 text-xs font-medium px-3 py-1 rounded-full">
                    {sectorLabel}
                  </span>
                  <span className="bg-white/15 backdrop-blur-sm text-white/90 text-xs font-medium px-3 py-1 rounded-full inline-flex items-center gap-1">
                    <IconPin className="w-3.5 h-3.5" />
                    {project.region}
                  </span>
                  {(isLabellise || isCertifie) && (
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${
                      isCertifie ? 'bg-green-500/20 text-green-300 border-green-400/30' : 'bg-amber/20 text-amber border-amber/30'
                    }`}>
                      {isCertifie ? t.projetDetail.labelCertified : t.projetDetail.labelLabeled}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h1 className="font-heading font-extrabold text-3xl md:text-5xl lg:text-6xl leading-tight text-white mb-6 drop-shadow-lg">
                  {project.title}
                </h1>

                {/* Metric cards — on masque la 3e card (tCO2 dispo) pour les projets agricoles */}
                <div className={`grid gap-3 max-w-lg ${isAgri ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  <div className="bg-black/40 backdrop-blur-md rounded-xl p-3 text-center border border-white/20">
                    <p className="font-heading font-bold text-white text-lg md:text-xl drop-shadow">{surfaceStr} ha</p>
                    <p className="text-white/70 text-xs">{t.projetDetail.surface}</p>
                  </div>
                  <div className="bg-black/40 backdrop-blur-md rounded-xl p-3 text-center border border-white/20">
                    <p className="font-heading font-bold text-white text-lg md:text-xl drop-shadow">{co2Str} t</p>
                    <p className="text-white/70 text-xs">CO₂</p>
                  </div>
                  {!isAgri && (
                    <div className="bg-black/40 backdrop-blur-md rounded-xl p-3 text-center border border-white/20">
                      <p className="font-heading font-bold text-white text-lg md:text-xl drop-shadow">
                        {project.nbArbres > 0
                          ? numFmt.format(project.nbArbres)
                          : `${numFmt.format(project.co2Disponible ?? 0)} t`}
                      </p>
                      <p className="text-white/70 text-xs">
                        {project.nbArbres > 0 ? t.projetDetail.trees : t.projetDetail.co2Available}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: photo thumbnails column */}
              <HeroThumbnails photos={photos} illustrations={illustrations} coverImage={coverImage} isFr={isFr} />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ MOBILE PHOTO GALLERY ═══════ */}
      <div className="md:hidden">
        <PhotoGallery photos={photos} illustrations={illustrations} coverImage={coverImage} isFr={isFr} />
      </div>

      {/* ═══════ BREADCRUMB ═══════ */}
      <div className="bg-cream pt-2 pb-2">
        <div className="container-site">
          <Link
            href={p('/projets')}
            className="text-forest/70 text-sm hover:text-forest transition-colors inline-flex items-center gap-1.5"
          >
            {t.projetDetail.backToList}
          </Link>
        </div>
      </div>

      {/* ═══════ MAIN CONTENT ═══════ */}
      <section className="section-padding bg-cream">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* ───── LEFT COLUMN ───── */}
            <div className="lg:col-span-2 space-y-8">

              {/* 1. Présentation du projet — Enhanced storytelling */}
              <Card>
                <SectionTitle icon={<IconClipboard className="w-5 h-5" />} title={isFr ? 'Présentation du projet' : 'Project presentation'} />
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  {/* Texte partagé avec la Synthèse de l'espace contributeur (fiche-textes.ts) ;
                      en français, la présentation rédigée par l'équipe le remplace quand elle existe. */}
                  <p className={presentationRedigee && isFr ? 'whitespace-pre-line' : undefined}>
                    {textes.presentation
                      ?? t.projetDetail.heroDescription(project.methode, surfaceStr, project.commune ?? null, co2Str, isLabellise || isCertifie)}
                  </p>
                </div>
                {/* Retombées socio-économiques : textes rédigés côté serveur
                    (src/lib/projets/socio-economie.ts) ; absent sans chiffre sûr */}
                {retombees && (
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <h3 className="font-heading font-bold text-base text-gray-900">{retombees.titre}</h3>
                    <p className="mt-2 flex flex-wrap items-baseline gap-x-2">
                      <span className="font-heading font-bold text-forest text-2xl">{retombees.chiffre.valeur}</span>
                      <span className="text-gray-700 text-sm">{retombees.chiffre.unite}</span>
                    </p>
                    <p className="mt-2 text-gray-600 leading-relaxed">{retombees.phrase}</p>
                    {retombees.detail?.length > 0 && (
                      <dl className="mt-3 space-y-1 text-sm">
                        {retombees.detail.map((l) => (
                          <div key={l.libelle} className={`flex flex-col sm:flex-row sm:justify-between sm:gap-4 ${l.horsTotal ? 'text-gray-500' : 'text-gray-700'}`}>
                            <dt>{l.libelle}</dt>
                            <dd className="font-medium sm:text-right sm:whitespace-nowrap">{l.valeur}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    <p className="mt-2 text-gray-500 text-xs leading-relaxed">{retombees.methode}</p>
                  </div>
                )}
              </Card>

              {/* 1b. Situation de départ (reconstitution) : pourquoi la forêt est morte, et
                  comment le projet y répond (règle du 21/09/2026). Icône calendrier : un
                  avant, distinct de la feuille des « Essences plantées » juste en dessous.
                  Phrases tirées du catalogue seulement (src/lib/projets/situation-depart.ts). */}
              {situationDepart && (
                <Card>
                  <SectionTitle icon={<IconCalendar className="w-5 h-5" />} title={situationDepart.titre} />
                  <div className="space-y-3 text-gray-600 leading-relaxed">
                    <p className="font-medium text-gray-800">{situationDepart.constat}</p>
                    {situationDepart.reponse.map((r) => <p key={r}>{r}</p>)}
                  </div>
                </Card>
              )}

              {/* 2. Essences (forestier) — remontées sous la présentation */}
              {isForestry && essencesDetailed.length > 0 && (
                <Card>
                  <SectionTitle icon={<IconLeaf className="w-5 h-5" />} title={isFr ? `Essences plantées (${essencesDetailed.length})` : `Planted species (${essencesDetailed.length})`} />
                  <p className="text-gray-500 text-sm mb-5">
                    {isFr
                      ? 'La diversité des essences renforce la résilience de la forêt face aux maladies et au changement climatique.'
                      : 'Species diversity strengthens forest resilience against diseases and climate change.'}
                  </p>
                  <div className="space-y-4">
                    {essencesDetailed.map((e, i) => (
                      <CarteEssence key={`${e.name}-${i}`} essence={e} isFr={isFr} />
                    ))}
                  </div>
                </Card>
              )}

              {/* 2b. Leviers agricoles — même position que les essences */}
              {isAgri && <AgriLeversSection type={project.type} methode={project.methode} leviers={leviers} isFr={isFr} />}

              {/* 3. Impacts & Co-bénéfices — Unified section */}
              <Card>
                <SectionTitle icon={<IconLeaf className="w-5 h-5" />} title={isFr ? 'Impacts environnementaux & sociaux' : 'Environmental & social impacts'} />
                <p className="text-gray-500 text-sm mb-6">
                  {isFr
                    ? 'Au-delà de son effet sur le climat, chaque projet agit sur l\'environnement et le territoire. Voici l\'évaluation de ce projet, dimension par dimension.'
                    : 'Beyond its climate effect, each project acts on the environment and the local area. Here is the assessment of this project, dimension by dimension.'}
                </p>

                {/* Radar */}
                <div className="mb-8">
                  <ImpactRadar result={impactResult} showLabels showLegend={false} />
                </div>

                {/* Combined Impact + Co-benefit detailed cards */}
                <div className="space-y-6 border-t border-gray-100 pt-6">
                  {(() => {
                    // Texte rédigé pour ce projet s'il existe, sinon le gabarit de sa méthode (textesFiche)
                    const d = textes.dimensions
                    const dimensions = [
                      { key: 'climat', label: isFr ? 'Climat' : 'Climate', coKey: null, explanation: d.climat },
                      { key: 'biodiversite', label: isFr ? 'Biodiversité' : 'Biodiversity', coKey: 'biodiversite', explanation: d.biodiversite },
                      { key: 'eau', label: isFr ? 'Eau' : 'Water', coKey: 'eau', explanation: d.eau },
                      { key: 'sols', label: isFr ? 'Sols' : 'Soils', coKey: 'sol', explanation: d.sols },
                      { key: 'socioEco', label: isFr ? 'Socio-économique' : 'Socio-economic', coKey: 'social', explanation: d.socioEco },
                    ]

                    return dimensions.map(({ key, label, coKey, explanation }) => {
                      const impactScore = impactResult.scores[key as keyof typeof impactResult.scores] ?? 0
                      const color = DIMENSION_COLORS[key as keyof typeof DIMENSION_COLORS] ?? '#666'
                      const coScore = coKey ? coBenefices.find(c => c.cobenefice.key === coKey)?.score ?? 0 : 0
                      const coLabel = coScore > 0 ? getScoreLabel(coScore, locale) : null

                      return (
                        <div key={key} className="p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2.5">
                              {coKey && <CoBeneficeIcon type={coKey} color={color} size={22} />}
                              {!coKey && <span style={{ color }}><Pictogram name={DIMENSION_ICONS[key as keyof typeof DIMENSION_ICONS]} className="w-[22px] h-[22px]" /></span>}
                              <span className="font-semibold text-gray-900">{label}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              {coLabel && (
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${color}15`, color }}>{coLabel}</span>
                              )}
                              <span className="text-sm font-bold" style={{ color }}>{impactScore}%</span>
                            </div>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                            <div className="h-full rounded-full transition-all" style={{ width: `${impactScore}%`, backgroundColor: color }} />
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed">{explanation}</p>
                        </div>
                      )
                    })
                  })()}
                </div>

                {/* Link to impact page */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <Link href={p('/impact')} className="text-sm text-forest font-medium hover:underline">
                    {isFr ? 'Comprendre notre méthodologie d\'évaluation →' : 'Learn about our assessment methodology →'}
                  </Link>
                </div>
              </Card>

              {/* 4. ODD — Sustainable Development Goals */}
              {odds.length > 0 && (
                <Card>
                  <SectionTitle icon={<IconTarget className="w-5 h-5" />} title={isFr ? 'Objectifs de Développement Durable' : 'Sustainable Development Goals'} />
                  <p className="text-gray-500 text-sm mb-5">
                    {isFr
                      ? `Les 17 ODD sont un cadre mondial défini par les Nations Unies pour construire un avenir durable d'ici 2030. Ce projet contribue directement à ${odds.length} d'entre eux. Voici pourquoi.`
                      : `The 17 SDGs are a global framework defined by the United Nations to build a sustainable future by 2030. This project directly contributes to ${odds.length} of them. Here's why.`}
                  </p>
                  <div className="space-y-4">
                    {odds.map((odd) => {
                      const oddExplanation = explicationODD(odd.numero, project, { isFr, nbEssences: essencesList.length, leviers: leviersDuProjet })
                      return (
                        <div key={odd.numero} className="p-4 rounded-xl border border-gray-100 hover:shadow-sm transition-shadow">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-heading font-bold text-base flex-shrink-0" style={{ backgroundColor: odd.couleur }}>
                              {odd.numero}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-900 leading-tight">{isFr ? odd.titre : (odd.titreEn || odd.titre)}</p>
                              <p className="text-gray-500 text-xs mt-1 leading-relaxed">{isFr ? odd.description : (odd.descriptionEn || odd.description)}</p>
                            </div>
                          </div>
                          <div className="mt-3 p-3 bg-sage/30 rounded-lg">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <span className="font-semibold text-forest">{isFr ? 'Pourquoi ce projet :' : 'Why this project:'}</span>{' '}
                              {oddExplanation}
                            </p>
                          </div>
                          <a
                            href={`https://sdgs.un.org/goals/goal${odd.numero}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-forest/70 hover:text-forest mt-2 inline-block"
                          >
                            {isFr ? `En savoir plus sur l'ODD ${odd.numero} →` : `Learn more about SDG ${odd.numero} →`}
                          </a>
                        </div>
                      )
                    })}
                  </div>
                </Card>
              )}

              {/* 5. Timeline / Chronologie */}
              {timeline.length > 0 && (
                <Card>
                  <SectionTitle icon={<IconCalendar className="w-5 h-5" />} title={isFr ? 'Chronologie du projet' : 'Project timeline'} />
                  <div className="relative mt-2">
                    {/* Vertical line — centered on the circles (left-[7px] = center at 8px = circle center) */}
                    <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-forest via-forest/50 to-gray-200" />
                    <div className="space-y-5">
                      {timeline.map((step, i) => (
                        <div key={i} className="flex items-start gap-4">
                          {/* Circle — 16px wide, center at 8px = aligned with line */}
                          <div
                            className={`w-4 h-4 shrink-0 rounded-full border-2 mt-0.5 ${
                              step.current
                                ? 'bg-white border-forest ring-2 ring-forest/30'
                                : step.done
                                ? 'bg-forest border-white'
                                : 'bg-gray-300 border-white'
                            }`}
                            style={{ boxShadow: step.done && !step.current ? '0 0 0 3px rgba(27,77,62,0.15)' : 'none' }}
                          />
                          {/* Text content */}
                          <div className="min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <p className={`text-sm font-semibold ${
                                step.current ? 'text-forest' : step.done ? 'text-forest' : 'text-gray-500'
                              }`}>
                                {step.label}
                              </p>
                              {step.date && (
                                <span className={`text-xs ${step.done ? 'text-forest/60' : 'text-gray-500'}`}>
                                  {step.date}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* 5 bis. Journal du projet — rien n'est affiché s'il est vide,
                  une rubrique « aucune actualité » ferait pire que le silence.
                  Version publique sobre : les grandes étapes, un titre, une
                  date et une phrase. Le suivi détaillé (chaque intervention,
                  chiffres, encadrés, photos) est réservé à l'espace des
                  entreprises qui financent le projet. */}
              {journal.length > 0 && (
                <Card>
                  <SectionTitle
                    icon={<IconClipboard className="w-5 h-5" />}
                    title={isFr ? 'Journal du projet' : 'Project journal'}
                  />
                  <p className="text-gray-500 text-sm -mt-2 mb-5">
                    {isFr
                      ? 'Les grandes étapes du projet. Le suivi détaillé est partagé avec les entreprises qui le financent, dans leur espace.'
                      : 'The main milestones of the project. Detailed monitoring is shared with the companies that fund it, in their private space.'}
                  </p>
                  <ol className="space-y-4">
                    {journal.map((entry) => (
                      <li key={entry.id} className="flex gap-4 border-l-2 border-sage pl-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-3 flex-wrap">
                            <time dateTime={entry.date} className="text-xs text-gray-500 tabular-nums">
                              {new Date(entry.date).toLocaleDateString(isFr ? 'fr-FR' : 'en-US', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </time>
                            {entry.auto && (
                              <span className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                {isFr ? 'repère automatique' : 'automated note'}
                              </span>
                            )}
                          </div>
                          <h3 className="text-base font-semibold text-gray-900 mt-0.5">{entry.titre}</h3>
                          {entry.phrase && <p className="text-sm text-gray-700 leading-relaxed mt-1">{entry.phrase}</p>}
                        </div>
                        {entry.photo && (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={entry.photo.url}
                            alt={entry.photo.alt || entry.titre}
                            loading="lazy"
                            className="w-20 h-20 sm:w-28 sm:h-20 shrink-0 object-cover rounded-lg bg-sage"
                          />
                        )}
                      </li>
                    ))}
                  </ol>
                </Card>
              )}

              {/* 6. Localisation + mini-carte */}
              <Card>
                <SectionTitle icon={<IconPin className="w-5 h-5" />} title={t.projetDetail.location} />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
                  {project.commune && <LocationField label={t.projetDetail.commune} value={project.commune} />}
                  {project.codePostal && <LocationField label={t.projetDetail.postalCode} value={project.codePostal} />}
                  {project.departement && (
                    <LocationField label={t.projetDetail.departement} value={`${project.departement} ${project.numeroDepartement ?? ''}`} />
                  )}
                  <LocationField label={t.projetDetail.region} value={project.region} />
                </div>
                {(agregats?.region || agregats?.methode) && (
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm mb-5">
                    {agregats.region && (
                      <Link href={agregats.region.href} className="text-forest font-medium hover:underline">
                        {isFr ? `Tous les projets en ${agregats.region.nom}` : `All projects in ${agregats.region.nom}`} →
                      </Link>
                    )}
                    {agregats.methode && (
                      <Link href={agregats.methode.href} className="text-forest font-medium hover:underline">
                        {isFr ? `Tous les projets ${agregats.methode.nom}` : `All ${agregats.methode.nom} projects`} →
                      </Link>
                    )}
                  </div>
                )}
                {project.coords && (
                  <LazyMount placeholderClassName="h-72 rounded-xl bg-gray-100 animate-pulse">
                    <MiniMap
                      lat={project.coords.lat}
                      lon={project.coords.lon}
                      label={project.title}
                      zoom={6}
                      height="h-72"
                    />
                  </LazyMount>
                )}
              </Card>

              {/* 7. Méthode Label Bas-Carbone — à la fin */}
              {ctx && (
                <Card>
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <SectionTitle icon={<IconClipboard className="w-5 h-5" />} title={`${t.projetDetail.method} · ${ctx.name}`} />
                    {ctx.duration && (
                      <span className="text-xs bg-sage text-forest px-2 py-0.5 rounded-full font-medium">
                        {t.projetDetail.methodDuration} : {ctx.duration}
                      </span>
                    )}
                  </div>

                  {/* Scientific summary */}
                  <div className="p-4 bg-sage/20 rounded-xl mb-4">
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">{ctx.description}</p>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {isFr
                        ? `Le Label Bas-Carbone est le cadre officiel du ministère de la Transition écologique pour les projets de réduction d'émissions menés en France. Un projet est labellisé sur dossier, selon une méthode approuvée par le ministère. Ses réductions d'émissions sont ensuite vérifiées par un auditeur indépendant, avant d'être reconnues par le ministère.`
                        : `The Label Bas-Carbone is the official framework of the French Ministry of Ecological Transition for emission reduction projects carried out in France. A project is labelled on the basis of its application, following a method approved by the ministry. Its emission reductions are then verified by an independent auditor before being recognised by the ministry.`}
                    </p>
                  </div>

                  {/* Method steps */}
                  <div className="space-y-2 mb-4">
                    <p className="text-sm font-semibold text-gray-800">
                      {isFr ? 'Comment ça fonctionne :' : 'How it works:'}
                    </p>
                    {[
                      isFr ? '1. Le porteur de projet dépose un dossier décrivant le scénario de référence (sans projet) et le scénario projet.' : '1. The project owner submits a file describing the reference scenario (without project) and the project scenario.',
                      isFr ? '2. La différence entre les deux scénarios donne les réductions d\'émissions nettes, exprimées en tonnes de CO₂ équivalent.' : '2. The difference between the two scenarios gives the net emission reductions, expressed in tons of CO₂ equivalent.',
                      isFr ? '3. Un auditeur indépendant vérifie les données sur le terrain et valide les calculs.' : '3. An independent auditor verifies the data in the field and validates the calculations.',
                      isFr ? '4. Le ministère délivre le Label Bas-Carbone et les crédits carbone sont émis.' : '4. The ministry issues the Low-Carbon Label and carbon credits are issued.',
                      ctx.duration
                        ? (isFr ? `5. Le projet est suivi pendant ${ctx.duration} avec des vérifications périodiques.` : `5. The project is monitored for ${ctx.duration} with periodic verifications.`)
                        : (isFr ? '5. Le projet est suivi avec des vérifications périodiques.' : '5. The project is monitored with periodic verifications.'),
                    ].map((step, i) => (
                      <p key={i} className="text-sm text-gray-600 leading-relaxed pl-1">{step}</p>
                    ))}
                  </div>

                  <div className="pt-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://label-bas-carbone.ecologie.gouv.fr/presentation-des-methodes-du-label-bas-carbone"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-forest font-medium hover:underline"
                    >
                      {isFr ? 'Voir toutes les méthodes du Label Bas-Carbone →' : 'View all Low-Carbon Label methods →'}
                    </a>
                    <a
                      href="https://label-bas-carbone.ecologie.gouv.fr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-500 hover:text-forest hover:underline"
                    >
                      {isFr ? 'Site officiel du Label Bas-Carbone →' : 'Official Low-Carbon Label website →'}
                    </a>
                  </div>
                </Card>
              )}

            </div>

            {/* ───── RIGHT SIDEBAR ───── */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-gray-100 lg:sticky lg:top-24">
                {/* CTA principal — en tête de colonne pour rester visible sans
                    défiler sur un écran classique ; la colonne reste collante
                    (`lg:sticky`) pour qu'il reste à l'écran pendant le défilement
                    (audit UI, 25/09/2026). Cible inchangée : même route de contact
                    que le CTA existant plus bas. */}
                <Link href={contactCtaHref(locale)} className="btn-secondary w-full text-center block mb-5">
                  {t.projetDetail.contactCta}
                </Link>
                <h3 className="font-heading font-bold text-lg text-gray-900 mb-4">{t.projetDetail.statusTitle}</h3>
                <div className="space-y-3">
                  <SidebarRow label={t.projetDetail.method} value={project.methode} />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t.projetDetail.status}</span>
                    <span className={`font-semibold px-2 py-0.5 rounded-full text-xs ${
                      isCertifie ? 'bg-green-50 text-green-700' : 'bg-amber/10 text-amber'
                    }`}>
                      {statusText}
                    </span>
                  </div>
                </div>

                {/* ODD mini badges */}
                {odds.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">ODD</p>
                    <div className="flex flex-wrap gap-1.5">
                      {odds.map((odd) => (
                        <span key={odd.numero} className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: odd.couleur }} title={odd.titre}>
                          {odd.numero}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Impact radar diagram */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3">
                    {isFr ? 'Score impact' : 'Impact score'}
                  </p>
                  <ImpactRadar result={impactResult} showLabels showLegend={false} />
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100">
                  <Link href={p('/contact')} className="btn-primary w-full text-center text-sm">
                    {t.projetDetail.contactCta}
                  </Link>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    {t.projetDetail.contactCtaHint}
                  </p>
                  {/* Fiche PDF publique : données de cette page seulement */}
                  <a
                    href={`/api/projets/${project.slug}/fiche-pdf`}
                    target="_blank"
                    rel="noopener nofollow"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-forest/30 px-4 py-2.5 text-sm font-medium text-forest hover:bg-forest/5"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></svg>
                    {isFr ? 'Télécharger la fiche' : 'Download the project sheet'}
                  </a>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500 leading-relaxed">
                  <p className="font-semibold text-gray-700 mb-1">{t.projetDetail.aboutLabelTitle}</p>
                  <p>{t.projetDetail.aboutLabelText}</p>
                </div>
              </div>

              <p className="text-[10px] text-gray-500 text-center">{coverImage.credit}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ SIMILAR PROJECTS — Horizontal scroll ═══════ */}
      {similarProjects.length > 0 && (
        <section className="bg-cream pb-12">
          <div className="container-site">
            <div className="flex items-center gap-2 mb-6">
              <IconLink className="w-5 h-5 text-forest" />
              <h2 className="font-heading font-bold text-xl text-gray-900">
                {isFr ? 'Projets similaires' : 'Similar projects'}
              </h2>
            </div>
          </div>
          {/* Full-bleed horizontal scroll container */}
          <div className="overflow-x-auto scrollbar-hide pb-4">
            <div className="flex gap-5 px-[max(1rem,calc((100vw-1480px)/2))]" style={{ width: 'max-content' }}>
              {similarProjects.map((sp, i) => (
                <div key={sp.slug} className="w-[300px] flex-shrink-0">
                  <ProjectCard
                    title={sp.title}
                    slug={sp.slug}
                    type={sp.type}
                    region={sp.region}
                    commune={sp.commune ?? undefined}
                    surface={sp.surface}
                    co2Tonnes={sp.co2Total}
                    featuredImage={sp.coverImage ?? undefined}
                    impactAverage={sp.impactAverage}
                    index={i}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════ COMPANY TESTIMONIAL (if exists) ═══════ */}
      {(() => {
        const companyTestimonials = temoignages
        if (companyTestimonials.length === 0) return null
        return (
        <section className="py-14 bg-white">
          <div className="container-site max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-block bg-forest/10 text-forest font-heading font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-3">
                {isFr ? 'Partenaire de ce projet' : 'Project partner'}
              </span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-gray-900">
                {companyTestimonials.length > 1
                  ? (isFr ? 'Ils soutiennent ce projet' : 'They support this project')
                  : (isFr ? `${companyTestimonials[0]!.companyName} soutient ce projet` : `${companyTestimonials[0]!.companyName} supports this project`)}
              </h2>
            </div>
            <div className={`grid gap-6 ${companyTestimonials.length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'max-w-2xl mx-auto'}`}>
              {companyTestimonials.map((ct) => (
                <Link
                  key={ct.companySlug}
                  href={p(`/entreprises/${ct.companySlug}`)}
                  className="block bg-cream rounded-2xl p-6 border border-gray-100 hover:border-forest/20 hover:shadow-md transition-all group"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center font-heading font-bold text-sm flex-shrink-0 text-white overflow-hidden"
                      style={{ backgroundColor: ct.color }}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={ct.logoUrl}
                        alt={ct.companyName}
                        className="w-full h-full object-contain p-1"
                        onError={(e) => {
                          const el = e.currentTarget
                          el.style.display = 'none'
                          if (el.nextElementSibling) (el.nextElementSibling as HTMLElement).style.display = 'flex'
                        }}
                      />
                      <span className="hidden items-center justify-center w-full h-full">
                        {ct.initials}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-sm text-gray-900 group-hover:text-forest transition-colors">
                        {ct.companyName}
                      </h3>
                      <p className="text-xs text-gray-500">{isFr ? ct.sector.fr : ct.sector.en}</p>
                    </div>
                  </div>
                  <blockquote className="text-sm text-gray-600 leading-relaxed italic mb-3">
                    &ldquo;{isFr ? ct.quote.fr : ct.quote.en}&rdquo;
                  </blockquote>
                  {ct.author && (
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        <span className="font-medium text-gray-500">{ct.author}</span>
                        {ct.role && <span> — {isFr ? ct.role.fr : ct.role.en}</span>}
                      </div>
                      <span className="text-xs font-medium text-forest opacity-0 group-hover:opacity-100 transition-opacity">
                        {isFr ? 'Voir la fiche →' : 'View page →'}
                      </span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
        )
      })()}

      <CTASection
        badge={t.projetDetail.ctaBadge}
        title={t.projetDetail.ctaTitle}
        subtitle={t.projetDetail.ctaSubtitle}
        primaryCTA={{ label: t.projetDetail.ctaPrimary, href: p('/contact') }}
        secondaryCTA={{ label: t.projetDetail.ctaSecondary, href: p('/projets') }}
      />

      {/* ═══════ BARRE D'ACTION MOBILE — CTA toujours visible sous lg ═══════ */}
      {/* Même cible que le CTA de la colonne latérale. `env(safe-area-inset-bottom)`
          évite la zone d'encoche/home-indicator ; `pb-24` plus haut compense sa hauteur. */}
      <div
        className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur-sm border-t border-gray-200 px-4 pt-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <Link href={contactCtaHref(locale)} className="btn-secondary w-full text-center block">
          {t.projetDetail.contactCta}
        </Link>
      </div>
    </div>
  )
}

// ─── Sub-components ───

function MetricCard({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
      <div className="flex justify-center mb-1.5">{icon}</div>
      <p className="font-heading font-bold text-forest text-lg">{value}</p>
      <p className="text-gray-500 text-xs">{label}</p>
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100">
      {children}
    </div>
  )
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <div className="text-forest">{icon}</div>
      <h2 className="font-heading font-bold text-xl text-gray-900">{title}</h2>
    </div>
  )
}

function LocationField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-gray-500 text-xs">{label}</p>
      <p className="font-medium text-gray-900">{value}</p>
    </div>
  )
}

function SidebarRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={`font-semibold text-gray-900 max-w-[55%] text-right truncate ${mono ? 'font-mono text-xs text-gray-600' : ''}`}>
        {value}
      </span>
    </div>
  )
}

// ─── Co-bénéfice bar ───

function CoBeneficeBar({ cobenefice, score }: { cobenefice: CoBenefice; score: number }) {
  const percentage = (score / 5) * 100

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2.5">
          <CoBeneficeIcon type={cobenefice.key} color={cobenefice.couleur} size={20} />
          <span className="font-semibold text-gray-900 text-sm">{cobenefice.nom}</span>
        </div>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${cobenefice.couleur}15`, color: cobenefice.couleur }}>
          {getScoreLabel(score)}
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${percentage}%`, backgroundColor: cobenefice.couleur }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{cobenefice.description}</p>
    </div>
  )
}

// ─── Carbon gauge ───

function CarbonGauge({ label, value, max, color, unit, numFmt }: {
  label: string; value: number; max: number; color: string; unit: string; numFmt: Intl.NumberFormat
}) {
  const pct = Math.min((value / max) * 100, 100)
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="font-heading font-bold text-lg" style={{ color }}>
          {numFmt.format(Math.round(value))} <span className="text-xs font-normal text-gray-500">{unit}</span>
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  )
}

// ─── Levers section (agricultural) ───

function AgriLeversSection({ type, methode, leviers: resolus, isFr }: {
  type: string
  methode: string
  /** Leviers résolus côté serveur : ceux du projet, sinon ceux de la méthode. */
  leviers: { liste: LevierAgricole[]; specifiquesAuProjet: boolean }
  isFr: boolean
}) {
  const leviers = resolus.liste
  if (leviers.length === 0) return null

  const isProjectSpecific = resolus.specifiquesAuProjet

  return (
    <Card>
      <SectionTitle
        icon={<IconWrench className="w-5 h-5" />}
        title={isFr
          ? `Leviers agricoles${isProjectSpecific ? ` de ce projet` : ` — méthode ${methode}`} (${leviers.length})`
          : `Agricultural levers${isProjectSpecific ? ` for this project` : ` — method ${methode}`} (${leviers.length})`}
      />
      <p className="text-gray-500 text-sm mb-5">
        {isProjectSpecific
          ? (isFr ? 'Pratiques retenues par l\'exploitant dans le plan d\'action de ce projet.' : 'Practices chosen by the farmer in this project\'s action plan.')
          : (isFr ? `Pratiques possibles dans cette méthode pour ${objectifLeviers(type, isFr)}.` : `Possible practices under this method to ${objectifLeviers(type, isFr)}.`)}
      </p>
      <div className="space-y-4">
        {leviers.map((levier) => (
          <LevierCard key={levier.slug} levier={levier} isFr={isFr} />
        ))}
      </div>
    </Card>
  )
}

/** Ce que visent les leviers, selon la méthode : SOBAC ne stocke rien dans les sols. */
function objectifLeviers(type: string, isFr: boolean): string {
  if (type === 'reduction_intrants') return isFr ? 'réduire les engrais azotés minéraux et les émissions qui y sont liées' : 'reduce mineral nitrogen fertilisers and the related emissions'
  if (type === 'carbon_agri' || type === 'elevage') return isFr ? "réduire les émissions de l'exploitation, et parfois stocker du carbone dans les prairies et les haies" : "reduce the farm's emissions, and sometimes store carbon in grasslands and hedges"
  if (type === 'grandes_cultures') return isFr ? 'réduire les émissions et stocker du carbone dans les sols' : 'reduce emissions and store carbon in soils'
  return isFr ? 'réduire les émissions ou stocker du carbone' : 'reduce emissions or store carbon'
}

/**
 * Badge « impact climat » d'un levier : la seule appréciation (« Fort »,
 * « Moyen à fort »), sans les chiffres qui la suivaient dans le catalogue
 * (« 40 à 60 % des émissions… ») et qui n'ont pas de source (contrôle du 08/10/2026).
 */
const appreciationClimat = (impact: string) => impact.split(/\s+—\s+/)[0]!.trim()

function LevierCard({ levier, isFr }: { levier: LevierAgricole; isFr: boolean }) {
  return (
    <div className="rounded-xl border border-gray-100 overflow-hidden hover:shadow-sm transition-shadow">
      <div className="flex flex-col sm:flex-row">
        {/* Photo */}
        {levier.image && (
          <div className="relative w-full sm:w-44 h-36 sm:h-auto flex-shrink-0 bg-gray-100">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={levier.image}
              alt={levier.nom}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        {/* Content */}
        <div className="p-4 min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0"><Pictogram name={levier.icon} className="w-7 h-7" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <h4 className="font-heading font-bold text-gray-900 text-sm">{levier.nom}</h4>
                <span className="text-xs bg-forest/10 text-forest px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                  {isFr ? 'Impact climat' : 'Climate impact'} : {appreciationClimat(levier.impactClimat)}
                </span>
              </div>
              <p className="text-gray-600 text-xs leading-relaxed mt-2">{levier.description}</p>
              {/* Pas d’anecdote du catalogue : sans source, retirées
                  de la fiche publique (contrôle du 08/10/2026) */}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Chronologie : ./chronologie.ts (triée par date, testée)
